if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.confirmedOptions = new Map();
        this.addEventListener('change', (event) => {
          if (!this.requiresSelection || !event.target.closest('variant-selects')) return;
          if (event.target.name) this.confirmedOptions.set(event.target.name, event.target.value);
        }, true);
        this.addEventListener('submit', (event) => {
          if (!this.requiresSelection || this.selectionComplete()) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.enforceSelection();
          this.modalContent.querySelector('variant-selects input:not(:checked), variant-selects select')?.focus();
        }, true);

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        this.selectionObserver?.disconnect();
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        this.requiresSelection = this.dataset.requireSelection === 'true';
        this.confirmedOptions.clear();
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            if (this.requiresSelection) {
              this.enforceSelection();
              this.selectionObserver?.disconnect();
              this.selectionObserver = new MutationObserver(() => this.enforceSelection());
              this.selectionObserver.observe(this.modalContent, { childList: true, subtree: true });
            }

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      optionGroups() {
        return [...this.modalContent.querySelectorAll('variant-selects .product-form__input')];
      }

      selectionComplete() {
        return this.optionGroups().every((group) => {
          const control = group.querySelector('input[type="radio"], select');
          const selected = group.querySelector('input[type="radio"]:checked, select');
          return !control || Boolean(selected?.value && this.confirmedOptions.get(control.name) === selected.value);
        });
      }

      enforceSelection() {
        if (!this.requiresSelection) return;
        for (const group of this.optionGroups()) {
          const controls = [...group.querySelectorAll('input[type="radio"], select')];
          if (!controls.length) continue;
          const selected = group.querySelector('input[type="radio"]:checked, select');
          if (selected?.value && this.confirmedOptions.get(controls[0].name) === selected.value) continue;
          for (const control of controls) {
            if (control.tagName === 'SELECT') {
              let placeholder = control.querySelector('[data-osiris-placeholder]');
              if (!placeholder) {
                placeholder = document.createElement('option');
                placeholder.dataset.osirisPlaceholder = '';
                placeholder.value = '';
                placeholder.textContent = this.dataset.selectionMessage;
                placeholder.disabled = true;
                control.prepend(placeholder);
              }
              control.value = '';
            } else {
              control.checked = false;
              control.removeAttribute('checked');
            }
          }
          const selectedLabel = group.querySelector('[data-selected-value]');
          if (selectedLabel?.textContent) selectedLabel.textContent = '';
        }
        const complete = this.selectionComplete();
        const picker = this.modalContent.querySelector('variant-selects');
        if (!picker) return;
        let note = this.modalContent.querySelector('.osiris-selection-note');
        if (!note) {
          note = document.createElement('p');
          note.className = 'osiris-selection-note';
          note.setAttribute('role', 'status');
          note.textContent = this.dataset.selectionMessage;
          picker.before(note);
        }
        note.hidden = complete;
        if (!complete) {
          this.modalContent.querySelectorAll('product-form [type="submit"]').forEach((button) => {
            button.disabled = true;
          });
        }
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
